import React, { useEffect, useMemo } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { CILOVA_MISTA } from "../../data/cilovaMista";
import {
  STAFF_OPTIONS,
  SPEAKER_OPTIONS,
  MUSIC_TYPE_OPTIONS,
  MUSIC_PERSON_OPTIONS,
  VEHICLE_OPTIONS,
} from "../../utils/calendarRoles";
import { buildCalendarEventsFromOrder } from "../../utils/calendarEventFactory";
import { getEffectiveRange, rangesOverlap } from "../../utils/calendarConflicts";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  color: "#111827",
  marginBottom: 8,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  fontSize: 15,
  background: "#fff",
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 120,
  resize: "vertical",
};

const HRBITOV_ADDRESS_MAP = {
  "Hřbitov Český Krumlov": "Hřbitovní 149, 381 01 Český Krumlov",
  "Hřbitov Kaplice": "Linecká, 382 41 Kaplice",
  "Hřbitov Vyšší Brod": "",
  "Hřbitov Frymburk": "",
  "Hřbitov Horní Planá": "",
};

const KREMATORIUM_ADDRESS_MAP = {
  "Krematorium České Budějovice": "Pražská 2275/108, 370 10 České Budějovice",
  "Krematorium Tábor": "Soběslavská 2502, 390 02 Tábor",
  "Krematorium Strakonice": "",
  "Krematorium Písek": "",
};




function readToken() {
  try {
    return (
      localStorage.getItem("pohrebka_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function formatBusyTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
}

function getBusyLabel(label, busyFrom, busyTo) {
  const from = formatBusyTime(busyFrom);
  const to = formatBusyTime(busyTo);
  if (from && to) return `${label} • ${from}-${to}`;
  return `${label} • obsazeno`;
}

function upsertRoleAssignment(list, role, value, options) {
  const clean = String(value || "").trim();
  const current = Array.isArray(list) ? list : [];
  const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
  if (clean) {
    const found = (Array.isArray(options) ? options : []).find((x) => String(x?.id || "") === clean);
    next.push({
      role,
      personId: clean,
      personLabel: found?.label || clean,
    });
  }
  return next;
}

function upsertVehicleAssignment(list, role, value, options) {
  const clean = String(value || "").trim();
  const current = Array.isArray(list) ? list : [];
  const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
  if (clean) {
    const found = (Array.isArray(options) ? options : []).find((x) => String(x?.id || "") === clean);
    next.push({
      role,
      vehicleId: clean,
      vehicleLabel: found?.label || clean,
    });
  }
  return next;
}

function getEventPeopleSet(event) {
  return new Set(
    (Array.isArray(event?.roleAssignments) ? event.roleAssignments : [])
      .map((x) => String(x?.personId || "").trim())
      .filter(Boolean)
  );
}

function getEventVehicleSet(event) {
  return new Set(
    (Array.isArray(event?.vehicleAssignments) ? event.vehicleAssignments : [])
      .map((x) => String(x?.vehicleId || "").trim())
      .filter(Boolean)
  );
}


function getAutoObradPresetFromPrubeh(prubehKey = "", typZakazky = "") {
  const p = String(prubehKey || "").trim();
  const t = String(typZakazky || "").trim();

  if (p === "obradni_sin_ck_nasledna_kremace") {
    return {
      misto: "Obřadní síň Český Krumlov",
      mistoId: "obradni_sin_ck",
      mistoSkupina: "obradni_sin",
      ceremonyVariant: "obradni_sin",
    };
  }

  if (p === "kostel_nasledna_kremace") {
    return {
      misto: "Kostel",
      mistoId: "kostel",
      mistoSkupina: "kostel",
      ceremonyVariant: "kostel",
      churchServiceType: "katolicky",
    };
  }

  if (p === "cb_krematorium_rozlouceni") {
    return {
      misto: "Krematorium České Budějovice",
      mistoId: "krematorium_cb",
      mistoSkupina: "krematorium",
      ceremonyVariant: "krematorium",
      krematorium: "Krematorium České Budějovice",
      krematoriumAdresa: "Pražská 2275/108, 370 10 České Budějovice",
    };
  }

  if (p === "obradni_sin_ck_hrob") {
    return {
      misto: "Obřadní síň Český Krumlov",
      mistoId: "obradni_sin_ck",
      mistoSkupina: "obradni_sin",
      ceremonyVariant: "obradni_sin",
      hrbitov: "Hřbitov Český Krumlov",
      hrbitovAdresa: "Hřbitovní 149, 381 01 Český Krumlov",
    };
  }

  if (p === "kostel_hrob") {
    return {
      misto: "Kostel",
      mistoId: "kostel",
      mistoSkupina: "kostel",
      ceremonyVariant: "kostel",
      churchServiceType: "katolicky",
      hrbitov: "Hřbitov Český Krumlov",
      hrbitovAdresa: "Hřbitovní 149, 381 01 Český Krumlov",
    };
  }

  if (p === "obrad_s_urnou_standard") {
    return {
      misto: "Obřadní síň Český Krumlov",
      mistoId: "obradni_sin_ck",
      mistoSkupina: "obradni_sin",
      ceremonyVariant: "obradni_sin",
    };
  }

  if (p === "bez_obradu" || p === "bez_obradu_urna") {
    return t === "kremace_bez_obradu"
      ? {
          krematorium: "Krematorium České Budějovice",
          krematoriumAdresa: "Pražská 2275/108, 370 10 České Budějovice",
        }
      : {};
  }

  if (p === "bez_obradu_vystaveni") {
    return t === "kremace_bez_obradu"
      ? {
          misto: "Obřadní síň Český Krumlov",
          mistoId: "obradni_sin_ck",
          mistoSkupina: "obradni_sin",
          ceremonyVariant: "obradni_sin",
          krematorium: "Krematorium České Budějovice",
          krematoriumAdresa: "Pražská 2275/108, 370 10 České Budějovice",
        }
      : {};
  }

  return {};
}

export default function Pohrebka2ObradForm() {
  const { order, ordersList, patchSection, activeOrderId } = usePohrebka2();
  const obrad = order?.obrad || {};
  const prevoz = order?.prevoz || {};
  const zaklad = order?.zaklad || {};
  const typZakazky = zaklad?.typZakazky || "";

  const setField = (name, value) => {
    patchSection("obrad", { [name]: String(value || "") });
  };

  const getRoleAssignment = (list, role) =>
    (Array.isArray(list) ? list : []).find((x) => String(x?.role || "") === String(role || ""));

  const getVehicleAssignment = (list, role) =>
    (Array.isArray(list) ? list : []).find((x) => String(x?.role || "") === String(role || ""));

  const setObradRole = (role, value) => {
    const clean = String(value || "").trim();
    const option = (STAFF_OPTIONS || []).find((x) => String(x?.id || "") === clean);
    const current = Array.isArray(obrad?.roleAssignments) ? obrad.roleAssignments : [];
    const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
    if (clean) {
      next.push({
        role,
        personId: clean,
        personLabel: option?.label || clean,
      });
    }
    patchSection("obrad", { roleAssignments: next });
  };

  const setObradVehicle = (role, value) => {
    const clean = String(value || "").trim();
    const option = (VEHICLE_OPTIONS || []).find((x) => String(x?.id || "") === clean);
    const current = Array.isArray(obrad?.vehicleAssignments) ? obrad.vehicleAssignments : [];
    const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
    if (clean) {
      next.push({
        role,
        vehicleId: clean,
        vehicleLabel: option?.label || clean,
      });
    }
    patchSection("obrad", { vehicleAssignments: next });
  };

  const setPrevozRole = (role, value) => {
    const clean = String(value || "").trim();
    const option = (STAFF_OPTIONS || []).find((x) => String(x?.id || "") === clean);
    const current = Array.isArray(prevoz?.roleAssignments) ? prevoz.roleAssignments : [];
    const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
    if (clean) {
      next.push({
        role,
        personId: clean,
        personLabel: option?.label || clean,
      });
    }
    patchSection("prevoz", { roleAssignments: next });
  };

  const setPrevozVehicle = (role, value) => {
    const clean = String(value || "").trim();
    const option = (VEHICLE_OPTIONS || []).find((x) => String(x?.id || "") === clean);
    const current = Array.isArray(prevoz?.vehicleAssignments) ? prevoz.vehicleAssignments : [];
    const next = current.filter((x) => String(x?.role || "") !== String(role || ""));
    if (clean) {
      next.push({
        role,
        vehicleId: clean,
        vehicleLabel: option?.label || clean,
      });
    }
    patchSection("prevoz", { vehicleAssignments: next });
  };

  const vedouciObradu = getRoleAssignment(obrad?.roleAssignments, "vedouci_obradu")?.personLabel || getRoleAssignment(obrad?.roleAssignments, "vedouci_obradu")?.personId || "";
  const obradniSluzba = getRoleAssignment(obrad?.roleAssignments, "obradni_sluzba")?.personLabel || getRoleAssignment(obrad?.roleAssignments, "obradni_sluzba")?.personId || "";
  const ridicPrevozu = getRoleAssignment(prevoz?.roleAssignments, "ridic_prevozu")?.personLabel || getRoleAssignment(prevoz?.roleAssignments, "ridic_prevozu")?.personId || "";
  const hlavniVuz = getVehicleAssignment(obrad?.vehicleAssignments, "hlavni_vuz")?.vehicleLabel || getVehicleAssignment(obrad?.vehicleAssignments, "hlavni_vuz")?.vehicleId || "";
  const prevozoveAuto = getVehicleAssignment(prevoz?.vehicleAssignments, "prevozove_auto")?.vehicleLabel || getVehicleAssignment(prevoz?.vehicleAssignments, "prevozove_auto")?.vehicleId || "";
  const currentOrderId =
    String(activeOrderId || order?.id || order?.orderId || order?.zakazkaId || "").trim();

  const otherCalendarEvents = useMemo(() => {
    return (Array.isArray(ordersList) ? ordersList : [])
      .flatMap((item) => buildCalendarEventsFromOrder(item || {}))
      .filter((ev) => String(ev?.orderId || "").trim() !== currentOrderId);
  }, [ordersList, currentOrderId]);

  const draftBaseOrder = useMemo(() => {
    return {
      ...(order || {}),
      id: currentOrderId || order?.id || order?.orderId || order?.zakazkaId || "",
      orderId: currentOrderId || order?.orderId || order?.id || order?.zakazkaId || "",
      zakazkaId: currentOrderId || order?.zakazkaId || order?.id || order?.orderId || "",
      obrad: { ...(order?.obrad || {}) },
      prevoz: { ...(order?.prevoz || {}) },
    };
  }, [order, currentOrderId]);

  const draftObradEvent = useMemo(() => {
    return buildCalendarEventsFromOrder(draftBaseOrder).find((x) => x?.typUdalosti === "obrad") || null;
  }, [draftBaseOrder]);

  const draftPrevozEvent = useMemo(() => {
    return buildCalendarEventsFromOrder(draftBaseOrder).find((x) => x?.typUdalosti === "prevoz") || null;
  }, [draftBaseOrder]);

  const availableLeaderOptions = useMemo(() => {
    return (STAFF_OPTIONS || []).map((item) => {
      const id = String(item?.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (id !== "dalsi" && draftObradEvent) {
        const probe = {
          ...draftObradEvent,
          roleAssignments: upsertRoleAssignment(
            obrad?.roleAssignments,
            "vedouci_obradu",
            id,
            STAFF_OPTIONS
          ),
        };
        const probeRange = getEffectiveRange(probe);

        for (const ev of otherCalendarEvents) {
          const evPeople = getEventPeopleSet(ev);
          if (!evPeople.has(id)) continue;

          const evRange = getEffectiveRange(ev);
          if (
            rangesOverlap(
              probeRange.effectiveStart,
              probeRange.effectiveEnd,
              evRange.effectiveStart,
              evRange.effectiveEnd
            )
          ) {
            disabled = true;
            busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
            busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
            break;
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [draftObradEvent, otherCalendarEvents, obrad?.roleAssignments]);

  const availableSupportOptions = useMemo(() => {
    return (STAFF_OPTIONS || []).map((item) => {
      const id = String(item?.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (id !== "dalsi") {
        if (vedouciObradu && id === String(vedouciObradu).trim()) {
          disabled = true;
        }

        if (!disabled && draftObradEvent) {
          const probe = {
            ...draftObradEvent,
            roleAssignments: upsertRoleAssignment(
              obrad?.roleAssignments,
              "obradni_sluzba",
              id,
              STAFF_OPTIONS
            ),
          };
          const probeRange = getEffectiveRange(probe);

          for (const ev of otherCalendarEvents) {
            const evPeople = getEventPeopleSet(ev);
            if (!evPeople.has(id)) continue;

            const evRange = getEffectiveRange(ev);
            if (
              rangesOverlap(
                probeRange.effectiveStart,
                probeRange.effectiveEnd,
                evRange.effectiveStart,
                evRange.effectiveEnd
              )
            ) {
              disabled = true;
              busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
              busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
              break;
            }
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [draftObradEvent, otherCalendarEvents, obrad?.roleAssignments, vedouciObradu]);

  const availableObradVehicleOptions = useMemo(() => {
    return (VEHICLE_OPTIONS || []).map((item) => {
      const id = String(item?.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (draftObradEvent) {
        const probe = {
          ...draftObradEvent,
          vehicleAssignments: upsertVehicleAssignment(
            obrad?.vehicleAssignments,
            "hlavni_vuz",
            id,
            VEHICLE_OPTIONS
          ),
        };
        const probeRange = getEffectiveRange(probe);

        for (const ev of otherCalendarEvents) {
          const evVehicles = getEventVehicleSet(ev);
          if (!evVehicles.has(id)) continue;

          const evRange = getEffectiveRange(ev);
          if (
            rangesOverlap(
              probeRange.effectiveStart,
              probeRange.effectiveEnd,
              evRange.effectiveStart,
              evRange.effectiveEnd
            )
          ) {
            disabled = true;
            busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
            busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
            break;
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [draftObradEvent, otherCalendarEvents, obrad?.vehicleAssignments]);

  const availablePrevozDriverOptions = useMemo(() => {
    return (STAFF_OPTIONS || []).map((item) => {
      const id = String(item?.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (id !== "dalsi" && draftPrevozEvent) {
        const probe = {
          ...draftPrevozEvent,
          roleAssignments: upsertRoleAssignment(
            prevoz?.roleAssignments,
            "ridic_prevozu",
            id,
            STAFF_OPTIONS
          ),
        };
        const probeRange = getEffectiveRange(probe);

        for (const ev of otherCalendarEvents) {
          const evPeople = getEventPeopleSet(ev);
          if (!evPeople.has(id)) continue;

          const evRange = getEffectiveRange(ev);
          if (
            rangesOverlap(
              probeRange.effectiveStart,
              probeRange.effectiveEnd,
              evRange.effectiveStart,
              evRange.effectiveEnd
            )
          ) {
            disabled = true;
            busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
            busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
            break;
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [draftPrevozEvent, otherCalendarEvents, prevoz?.roleAssignments]);

  const availablePrevozVehicleOptions = useMemo(() => {
    return (VEHICLE_OPTIONS || []).map((item) => {
      const id = String(item?.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (draftPrevozEvent) {
        const probe = {
          ...draftPrevozEvent,
          vehicleAssignments: upsertVehicleAssignment(
            prevoz?.vehicleAssignments,
            "prevozove_auto",
            id,
            VEHICLE_OPTIONS
          ),
        };
        const probeRange = getEffectiveRange(probe);

        for (const ev of otherCalendarEvents) {
          const evVehicles = getEventVehicleSet(ev);
          if (!evVehicles.has(id)) continue;

          const evRange = getEffectiveRange(ev);
          if (
            rangesOverlap(
              probeRange.effectiveStart,
              probeRange.effectiveEnd,
              evRange.effectiveStart,
              evRange.effectiveEnd
            )
          ) {
            disabled = true;
            busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
            busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
            break;
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [draftPrevozEvent, otherCalendarEvents, prevoz?.vehicleAssignments]);

  const handleKrematoriumChange = (value) => {
    const clean = String(value || "").trim();
    const found = (CILOVA_MISTA || []).find(
      (item) => item.typ === "krematorium" && String(item.nazev || "").trim() === clean
    );
    const adresa = found?.adresa || KREMATORIUM_ADDRESS_MAP[clean] || "";

    patchSection("obrad", {
      krematorium: clean,
      krematoriumAdresa: adresa,
    });

    if (String(typZakazky || "").trim() === "kremace" || String(typZakazky || "").trim() === "kremace_bez_obradu") {
      patchSection("prevoz", {
        cilPrevozuTyp: "krematorium",
        cilPrevozuNazev: clean,
        cilPrevozuAdresa: adresa,
      });
    }
  };

  const handleMistoChange = (value) => {
    patchSection("obrad", {
      misto: String(value || ""),
    });
  };

  const handleHrbitovChange = (value) => {
    const clean = String(value || "").trim();
    const adresa = HRBITOV_ADDRESS_MAP[clean] || "";

    patchSection("obrad", {
      hrbitov: clean,
      hrbitovAdresa: adresa,
    });

    if (String(typZakazky || "").trim() === "pohreb") {
      patchSection("prevoz", {
        cilPrevozuTyp: "hrbitov",
        cilPrevozuNazev: clean,
        cilPrevozuAdresa: adresa,
      });
    }
  };

  const isKremace = typZakazky === "kremace";
  const isKremaceBezObradu = typZakazky === "kremace_bez_obradu";
  const isPohreb = typZakazky === "pohreb";
  const isAlternativa = typZakazky === "alternativa";
  const isObradSUrnou = typZakazky === "obrad_s_urnou";
  const isPrevoz = typZakazky === "prevoz";
  const isOstatniOrEmpty = !typZakazky || typZakazky === "ostatni";
  const isKremaceBezObraduVystaveni =
    isKremaceBezObradu &&
    (
      String(zaklad?.prubehKey || "").trim() === "bez_obradu_vystaveni" ||
      String(obrad?.ceremonyVariant || "").trim() === "vystaveni"
    );


  useEffect(() => {
    const prubehKey = String(zaklad?.prubehKey || "").trim();
    const typZakazky = String(zaklad?.typZakazky || "").trim();

    if (!prubehKey || !typZakazky) return;

    const preset = getAutoObradPresetFromPrubeh(prubehKey, typZakazky);
    if (!preset || !Object.keys(preset).length) return;

    const patch = {};

    for (const [key, value] of Object.entries(preset)) {
      if (!String(obrad?.[key] || "").trim() && String(value || "").trim()) {
        patch[key] = value;
      }
    }

    if (typZakazky && String(obrad?.typ || "").trim() !== typZakazky) {
      if (typZakazky === "kremace") patch.typ = "kremace";
      else if (typZakazky === "kremace_bez_obradu") patch.typ = "kremace_bez_obradu";
      else if (typZakazky === "pohreb") patch.typ = "pohreb";
      else if (typZakazky === "alternativa") patch.typ = "alternativa";
      else if (typZakazky === "obrad_s_urnou") patch.typ = "obrad_s_urnou";
    }

    if (!Object.keys(patch).length) return;
    patchSection("obrad", patch);
  }, [
    zaklad?.prubehKey,
    zaklad?.typZakazky,
    obrad?.typ,
    obrad?.misto,
    obrad?.mistoId,
    obrad?.mistoSkupina,
    obrad?.ceremonyVariant,
    obrad?.churchServiceType,
    obrad?.hrbitov,
    obrad?.hrbitovAdresa,
    obrad?.krematorium,
    obrad?.krematoriumAdresa,
    patchSection,
  ]); // AUTO_OBRAD_FROM_PRUBEH

  useEffect(() => {
    const handler = (e) => {
      const date = String(e?.detail?.date || "").slice(0, 10);
      const time = String(e?.detail?.time || "").slice(0, 5);
      const typZakazky = String(e?.detail?.typZakazky || "").trim();
      const misto = String(e?.detail?.misto || "").trim();
      const mistoId = String(e?.detail?.mistoId || "").trim();
      const mistoSkupina = String(e?.detail?.mistoSkupina || "").trim();
      const ceremonyVariant = String(e?.detail?.ceremonyVariant || "").trim();
      const churchServiceType = String(e?.detail?.churchServiceType || "").trim();
      const mistoAdresa = String(e?.detail?.mistoAdresa || "").trim();
      const farar = String(e?.detail?.assignments?.farar || "").trim();
      const poznamka = String(e?.detail?.poznamka || "").trim();

      const leader = String(e?.detail?.assignments?.leader || "").trim();
      const support = String(e?.detail?.assignments?.support || "").trim();
      const speaker = String(e?.detail?.assignments?.speaker || "").trim();
      const speakerCustom = String(e?.detail?.assignments?.speakerCustom || "").trim();
      const musicType = String(e?.detail?.assignments?.musicType || "").trim();
      const musicPerson = String(e?.detail?.assignments?.musicPerson || "").trim();
      const musicPersonCustom = String(e?.detail?.assignments?.musicPersonCustom || "").trim();
      const vehicle = String(e?.detail?.assignments?.vehicle || "").trim();

      const staffLabelById = (id) =>
        (STAFF_OPTIONS || []).find((item) => String(item?.id || "").trim() === String(id || "").trim())?.label || id;

      const vehicleLabelById = (id) =>
        (VEHICLE_OPTIONS || []).find((item) => String(item?.id || "").trim() === String(id || "").trim())?.label || id;

      const resolvedSpeaker = speaker === "dalsi" ? speakerCustom : speaker;
      const resolvedMusicPerson = musicPerson === "dalsi" ? musicPersonCustom : musicPerson;

      const speakerLabelById = (id) =>
        (SPEAKER_OPTIONS || []).find((item) => String(item.id || "").trim() === String(id || "").trim())?.label || id;

      const musicPersonLabelById = (id) =>
        (MUSIC_PERSON_OPTIONS || []).find((item) => String(item.id || "").trim() === String(id || "").trim())?.label || id;

      const resolvedSpeakerLabel =
        speaker === "dalsi"
          ? speakerCustom
          : speakerLabelById(resolvedSpeaker);

      const resolvedMusicPersonLabel =
        musicPerson === "dalsi"
          ? musicPersonCustom
          : musicPersonLabelById(resolvedMusicPerson);

      const obradRoleAssignments = [
        leader
          ? {
              role: "vedouci_obradu",
              personId: leader,
              personLabel: staffLabelById(leader),
            }
          : null,
        support && support !== leader
          ? {
              role: "obradni_sluzba",
              personId: support,
              personLabel: staffLabelById(support),
            }
          : null,
        resolvedSpeaker
          ? {
              role: "recnik",
              personId: resolvedSpeaker,
              personLabel: resolvedSpeakerLabel || resolvedSpeaker,
            }
          : null,
        resolvedMusicPerson
          ? {
              role: "hudba",
              personId: resolvedMusicPerson,
              personLabel: resolvedMusicPersonLabel || resolvedMusicPerson,
            }
          : null,
      ].filter(Boolean);

      const obradVehicleAssignments =
        vehicle && typZakazky !== "prevoz"
          ? [
              {
                role: "hlavni_vuz",
                vehicleId: vehicle,
                vehicleLabel: vehicleLabelById(vehicle),
              },
            ]
          : [];

      const prevozRoleAssignments =
        typZakazky === "prevoz"
          ? [
              leader
                ? {
                    role: "ridic_prevozu",
                    personId: leader,
                    personLabel: staffLabelById(leader),
                  }
                : null,
              support && support !== leader
                ? {
                    role: "doprovod_prevozu",
                    personId: support,
                    personLabel: staffLabelById(support),
                  }
                : null,
            ].filter(Boolean)
          : [];

      const prevozVehicleAssignments =
        typZakazky === "prevoz" && vehicle
          ? [
              {
                role: "prevozove_auto",
                vehicleId: vehicle,
                vehicleLabel: vehicleLabelById(vehicle),
              },
            ]
          : [];

      let obradTyp = "";
      if (typZakazky === "kremace") obradTyp = "kremace";
      else if (typZakazky === "kremace_bez_obradu") obradTyp = "kremace_bez_obradu";
      else if (typZakazky === "pohreb") obradTyp = "pohreb";
      else if (typZakazky === "alternativa") obradTyp = "alternativa";
      else if (typZakazky === "obrad_s_urnou") obradTyp = "obrad_s_urnou";
      else if (typZakazky === "prevoz") obradTyp = "samostatny_prevoz";
      else if (typZakazky === "ostatni") obradTyp = "ostatni";

      if (typZakazky) {
        patchSection("zaklad", { typZakazky });
      }

      patchSection("obrad", {
        ...(date ? { datum: date } : {}),
        ...(time ? { cas: time } : {}),
        ...(obradTyp ? { typ: obradTyp } : {}),
        ...((typZakazky !== "prevoz" && misto && misto !== "__manual__") ? { misto } : {}),
        ...((typZakazky !== "prevoz" && mistoId) ? { mistoId } : {}),
        ...((typZakazky !== "prevoz" && mistoSkupina) ? { mistoSkupina } : {}),
        ...((typZakazky !== "prevoz" && ceremonyVariant) ? { ceremonyVariant } : {}),
        ...((typZakazky !== "prevoz" && churchServiceType) ? { churchServiceType } : {}),
        ...((typZakazky !== "prevoz" && mistoAdresa) ? { mistoAdresa } : {}),
        ...((typZakazky !== "prevoz" && farar) ? { fararJmeno: farar } : {}),
        ...(typZakazky !== "prevoz" ? { roleAssignments: obradRoleAssignments } : {}),
        ...(typZakazky !== "prevoz" ? { vehicleAssignments: obradVehicleAssignments } : {}),
        ...(typZakazky !== "prevoz" && speaker ? { recnikTyp: speaker } : {}),
        ...(typZakazky !== "prevoz" && speaker === "dalsi" && speakerCustom ? { recnikJmeno: speakerCustom } : {}),
        ...(typZakazky !== "prevoz" && musicType ? { hudbaTyp: musicType } : {}),
        ...(typZakazky !== "prevoz" && resolvedMusicPerson ? { hudbaZajistuje: resolvedMusicPerson } : {}),
        ...(poznamka ? { poznamka } : {}),
      });

      if (typZakazky === "prevoz") {
        patchSection("prevoz", {
          ...(date ? { datumPrevozu: date } : {}),
          ...(time ? { casPrevozu: time } : {}),
          ...((misto && misto !== "__manual__") ? { mistoOdvozu: misto } : {}),
          ...(mistoAdresa ? { adresaOdvozu: mistoAdresa } : {}),
          ...(poznamka ? { poznamka } : {}),
          roleAssignments: prevozRoleAssignments,
          vehicleAssignments: prevozVehicleAssignments,
        });
      }
    };

    window.addEventListener("pohrebka2-calendar-create-date", handler);
    return () => window.removeEventListener("pohrebka2-calendar-create-date", handler);
  }, [patchSection]);

  useEffect(() => {
    let nextTyp = "";
    if (typZakazky === "kremace") nextTyp = "kremace";
    else if (typZakazky === "kremace_bez_obradu") nextTyp = "kremace_bez_obradu";
    else if (typZakazky === "pohreb") nextTyp = "pohreb";
    else if (typZakazky === "alternativa") nextTyp = "alternativa";
    else if (typZakazky === "obrad_s_urnou") nextTyp = "obrad_s_urnou";
    else if (typZakazky === "prevoz") nextTyp = "samostatny_prevoz";
    else if (typZakazky === "ostatni") nextTyp = "ostatni";

    if ((obrad?.typ || "") !== nextTyp) {
      patchSection("obrad", { typ: nextTyp });
    }
  }, [typZakazky, obrad?.typ, patchSection]);

  useEffect(() => {
    if (typZakazky === "prevoz" || typZakazky === "obrad_s_urnou") return;

    const prevozPatch = {};

    if (!String(prevoz?.datumPrevozu || "").trim() && String(obrad?.datum || "").trim()) {
      prevozPatch.datumPrevozu = String(obrad.datum || "").trim();
    }

    if (!String(prevoz?.casPrevozu || "").trim() && String(obrad?.cas || "").trim()) {
      prevozPatch.casPrevozu = String(obrad.cas || "").trim();
    }

    if (typZakazky === "pohreb") {
      const hrbitov = String(obrad?.hrbitov || "").trim();
      const hrbitovAdresa = String(obrad?.hrbitovAdresa || "").trim();

      if (hrbitov && !String(prevoz?.cilPrevozuNazev || "").trim()) {
        prevozPatch.cilPrevozuNazev = hrbitov;
      }
      if (hrbitovAdresa && !String(prevoz?.cilPrevozuAdresa || "").trim()) {
        prevozPatch.cilPrevozuAdresa = hrbitovAdresa;
      }
      if (!String(prevoz?.cilPrevozuTyp || "").trim() && (hrbitov || hrbitovAdresa)) {
        prevozPatch.cilPrevozuTyp = "hrbitov";
      }
    }

    if (typZakazky === "kremace" || typZakazky === "kremace_bez_obradu") {
      const krematorium = String(obrad?.krematorium || "").trim();
      const krematoriumAdresa = String(obrad?.krematoriumAdresa || "").trim();

      if (krematorium && !String(prevoz?.cilPrevozuNazev || "").trim()) {
        prevozPatch.cilPrevozuNazev = krematorium;
      }
      if (krematoriumAdresa && !String(prevoz?.cilPrevozuAdresa || "").trim()) {
        prevozPatch.cilPrevozuAdresa = krematoriumAdresa;
      }
      if (!String(prevoz?.cilPrevozuTyp || "").trim() && (krematorium || krematoriumAdresa)) {
        prevozPatch.cilPrevozuTyp = "krematorium";
      }
    }

    if (Object.keys(prevozPatch).length) {
      patchSection("prevoz", prevozPatch);
    }
  }, [
    typZakazky,
    obrad?.datum,
    obrad?.cas,
    obrad?.hrbitov,
    obrad?.hrbitovAdresa,
    obrad?.krematorium,
    obrad?.krematoriumAdresa,
    prevoz?.datumPrevozu,
    prevoz?.casPrevozu,
    prevoz?.cilPrevozuTyp,
    prevoz?.cilPrevozuNazev,
    prevoz?.cilPrevozuAdresa,
    patchSection,
  ]);

  const showCeremonyCore =
    isKremace || isPohreb || isAlternativa || isObradSUrnou || isOstatniOrEmpty || isKremaceBezObraduVystaveni;
  const showKrematorium = isKremace || isKremaceBezObradu;
  const showHrbitov = isPohreb;
  const showCeremonyPeople = isKremace || isPohreb || isAlternativa || isObradSUrnou || isKremaceBezObraduVystaveni;
  const showCeremonyDateTime = isKremace || isPohreb || isAlternativa || isObradSUrnou || isKremaceBezObraduVystaveni;
  const showCeremonyPlace = isKremace || isPohreb || isAlternativa || isObradSUrnou || isKremaceBezObraduVystaveni;
  const showKrematoriumHelp = isKremaceBezObradu && !isKremaceBezObraduVystaveni;
  const ceremonyTitle =
    isObradSUrnou ? "Obřad s urnou" :
    isAlternativa ? "Alternativní rozloučení" :
    "Kremace / Pohřeb";

  const typDisplayLabel =
    typZakazky === "kremace" ? "Kremace" :
    typZakazky === "kremace_bez_obradu" ? "Kremace bez obřadu" :
    typZakazky === "pohreb" ? "Pohřeb" :
    typZakazky === "alternativa" ? "Alternativa" :
    typZakazky === "obrad_s_urnou" ? "Obřad s urnou" :
    typZakazky === "prevoz" ? "Samostatný převoz" :
    typZakazky === "ostatni" ? "Ostatní" :
    (obrad.typ || "");

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 34, fontWeight: 900, color: "#111827", lineHeight: 1.05 }}>
            {ceremonyTitle}
          </div>
          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 16 }}>
            Typ rozloučení, místo a základní organizační údaje.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700 }}>Aktivní zakázka</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>{activeOrderId || "-"}</div>
        </div>
      </div>

      <div style={{ marginBottom: 18, padding: 14, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>Převzato ze Základ</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
          {typZakazky === "kremace" && "Kremace s obřadem"}
          {typZakazky === "kremace_bez_obradu" && "Kremace bez obřadu"}
          {typZakazky === "pohreb" && "Pohřeb"}
          {typZakazky === "prevoz" && "Samostatný převoz"}
          {typZakazky === "ostatni" && "Ostatní"}
          {!typZakazky && "Zatím není zvolen typ zakázky"}
        </div>
      </div>

      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>Typ</label>
          <input
            type="text"
            value={typDisplayLabel}
            readOnly
            style={{ ...inputStyle, background: "#f3f4f6" }}
          />
        </div>

        {showCeremonyCore ? (
          <>
            <div>
              <label style={labelStyle}>Datum obřadu</label>
              <input
                type="date"
                value={obrad.datum || ""}
                onChange={(e) => setField("datum", e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Čas obřadu</label>
              <input
                type="time"
                value={obrad.cas || ""}
                onChange={(e) => setField("cas", e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Místo</label>
              <input
                type="text"
                value={obrad.misto || ""}
                onChange={(e) => handleMistoChange(e.target.value)}
                placeholder="Např. kostel Frymburk, obřadní síň..."
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Adresa místa obřadu</label>
              <input
                type="text"
                value={obrad.mistoAdresa || ""}
                onChange={(e) => setField("mistoAdresa", e.target.value)}
                placeholder="Např. Náměstí 1, 382 79 Frymburk"
                style={inputStyle}
              />
              <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
                Sem vždy piš skutečnou adresu místa rozloučení. Právě z této adresy se počítají km v Převozu.
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{
                padding: 12,
                borderRadius: 14,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.5
              }}>
                Technické údaje místa (ID místa, skupina místa, varianta rozloučení a typ kostelního rozloučení)
                se plní hlavně z kalendáře. Tady je ručně nepřepisujeme, aby se nerozbíjela logika blokací a návazností.
              </div>
            </div>

            <div>
              <label style={labelStyle}>Farář</label>
              <input
                type="text"
                value={obrad.fararJmeno || ""}
                onChange={(e) => setField("fararJmeno", e.target.value)}
                placeholder="Např. P. Novák"
                style={inputStyle}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                Farář může být vyplněný i bez civilního řečníka. Neznamená to automaticky mluvené slovo od řečnice.
              </div>
            </div>
          </>
        ) : null}

        {showKrematorium ? (
          <>
              <div>
                <label style={labelStyle}>Krematorium</label>
                <select
                  value={obrad.krematorium || ""}
                  onChange={(e) => handleKrematoriumChange(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">Vyber krematorium</option>
                  {(CILOVA_MISTA || [])
                    .filter((item) => item.typ === "krematorium")
                    .map((item) => (
                      <option key={item.nazev} value={item.nazev}>
                        {item.nazev}
                      </option>
                    ))}
                </select>
              </div>

            <div>
              <label style={labelStyle}>Adresa krematoria</label>
              <input
                type="text"
                value={obrad.krematoriumAdresa || ""}
                onChange={(e) => setField("krematoriumAdresa", e.target.value)}
                style={inputStyle}
              />
            </div>

            {showKrematoriumHelp ? (
              <div style={{ gridColumn: "1 / -1", fontSize: 13, color: "#6b7280", marginTop: -4 }}>
                U kremace bez obřadu zde řešíš pouze návaznost na krematorium. Samotné rozloučení se vyplňuje jen pokud je zadané následné rozloučení s urnou.
              </div>
            ) : null}
          </>
        ) : null}

          {showHrbitov ? (
            <>
              <div>
                <label style={labelStyle}>Hřbitov</label>
                <input
                  list="pohrebka2-hrbitovy"
                  type="text"
                  value={obrad.hrbitov || ""}
                  onChange={(e) => handleHrbitovChange(e.target.value)}
                  placeholder="Např. Hřbitov Český Krumlov"
                  style={inputStyle}
                />
                <datalist id="pohrebka2-hrbitovy">
                  {Object.keys(HRBITOV_ADDRESS_MAP).map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>
              </div>

              <div>
                <label style={labelStyle}>Adresa hřbitova</label>
                <input
                  type="text"
                  value={obrad.hrbitovAdresa || ""}
                  onChange={(e) => setField("hrbitovAdresa", e.target.value)}
                  placeholder="Adresa hřbitova"
                  style={inputStyle}
                />
              </div>
            </>
          ) : null}

        {showCeremonyPeople ? (
          <>
            <div>
              <label style={labelStyle}>Řečník / duchovní (text)</label>
              <input
                type="text"
                value={obrad.recnik || ""}
                onChange={(e) => setField("recnik", e.target.value)}
                placeholder="Volitelný doplňující text"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Řečnice / mluvené slovo</label>
              <select
                value={obrad.recnikTyp || ""}
                onChange={(e) => setField("recnikTyp", e.target.value)}
                style={inputStyle}
              >
                <option value="">Vyber řečnici / variantu</option>
                {(SPEAKER_OPTIONS || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>

            {String(obrad.recnikTyp || "") === "dalsi" ? (
              <div>
                <label style={labelStyle}>Jméno řečnice</label>
                <input
                  type="text"
                  value={obrad.recnikJmeno || ""}
                  onChange={(e) => setField("recnikJmeno", e.target.value)}
                  placeholder="Doplň řečnici ručně"
                  style={inputStyle}
                />
              </div>
            ) : null}

            <div>
              <label style={labelStyle}>Hudba (text)</label>
              <input
                type="text"
                value={obrad.hudba || ""}
                onChange={(e) => setField("hudba", e.target.value)}
                placeholder="Volitelný doplňující text"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Typ hudby</label>
              <select
                value={obrad.hudbaTyp || ""}
                onChange={(e) => setField("hudbaTyp", e.target.value)}
                style={inputStyle}
              >
                <option value="">Vyber typ hudby</option>
                {(MUSIC_TYPE_OPTIONS || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>

            {String(obrad.hudbaTyp || "") === "reprodukovana" ? (
              <>
                <div>
                  <label style={labelStyle}>Kdo pouští hudbu</label>
                  <select
                    value={obrad.hudbaZajistuje || ""}
                    onChange={(e) => setField("hudbaZajistuje", e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Vyber člověka</option>
                    {(MUSIC_PERSON_OPTIONS || []).map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </select>
                </div>

                {String(obrad.hudbaZajistuje || "") === "dalsi" ? (
                  <div>
                    <label style={labelStyle}>Hudbu zajišťuje — doplnění</label>
                    <input
                      type="text"
                      value={obrad.hudbaZajistujeJmeno || ""}
                      onChange={(e) => setField("hudbaZajistujeJmeno", e.target.value)}
                      placeholder="Doplň hudbu ručně"
                      style={inputStyle}
                    />
                  </div>
                ) : null}
              </>
            ) : null}

            <div>
              <label style={labelStyle}>Vedoucí obřadu</label>
              <select
                value={vedouciObradu}
                onChange={(e) => setObradRole("vedouci_obradu", e.target.value)}
                style={inputStyle}
              >
                <option value="">Vyber člověka</option>
                {availableLeaderOptions.map((item) => (
                  <option key={item.id} value={item.id} disabled={!!item.disabled}>
                    {item.displayLabel || item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Obřadní služba / druhý člověk</label>
              <select
                value={obradniSluzba}
                onChange={(e) => setObradRole("obradni_sluzba", e.target.value)}
                style={inputStyle}
              >
                <option value="">Vyber druhého člověka</option>
                {availableSupportOptions.map((item) => (
                  <option key={item.id} value={item.id} disabled={!!item.disabled}>
                    {item.displayLabel || item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Auto pro obřad</label>
              <select
                value={hlavniVuz}
                onChange={(e) => setObradVehicle("hlavni_vuz", e.target.value)}
                style={inputStyle}
              >
                <option value="">Bez auta / nevyplněno</option>
                {availableObradVehicleOptions.map((item) => (
                  <option key={item.id} value={item.id} disabled={!!item.disabled}>
                    {item.displayLabel || item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Řidič převozu</label>
              <select
                value={ridicPrevozu}
                onChange={(e) => setPrevozRole("ridic_prevozu", e.target.value)}
                style={inputStyle}
              >
                <option value="">Vyber člověka</option>
                {availablePrevozDriverOptions.map((item) => (
                  <option key={item.id} value={item.id} disabled={!!item.disabled}>
                    {item.displayLabel || item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Auto pro převoz</label>
              <select
                value={prevozoveAuto}
                onChange={(e) => setPrevozVehicle("prevozove_auto", e.target.value)}
                style={inputStyle}
              >
                <option value="">Bez auta / nevyplněno</option>
                {availablePrevozVehicleOptions.map((item) => (
                  <option key={item.id} value={item.id} disabled={!!item.disabled}>
                    {item.displayLabel || item.label}
                  </option>
                ))}
              </select>
            </div>

            {(availableLeaderOptions.find((x) => x.id === vedouciObradu && x.disabled) ||
              availableSupportOptions.find((x) => x.id === obradniSluzba && x.disabled) ||
              availableObradVehicleOptions.find((x) => x.id === hlavniVuz && x.disabled) ||
              availablePrevozDriverOptions.find((x) => x.id === ridicPrevozu && x.disabled) ||
              availablePrevozVehicleOptions.find((x) => x.id === prevozoveAuto && x.disabled)) ? (
              <div style={{
                gridColumn: "1 / -1",
                fontSize: 13,
                color: "#991b1b",
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: 12,
                padding: "10px 12px",
                lineHeight: 1.5,
              }}>
                Některé vybrané role nebo auta jsou v kolizi s hlavním kalendářem. Ve výběru jsou blokované stejně jako v kalendáři.
              </div>
            ) : null}
          </>
        ) : null}

        {isPrevoz ? (
          <div style={{ gridColumn: "1 / -1", padding: 14, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
            Tohle je samostatná zakázka převozu. Hlavní údaje patří do záložky Převoz — například převoz z místa úmrtí na pitvu, do chladnice nebo do lednice.
          </div>
        ) : null}

          {isKremaceBezObraduVystaveni ? (
            <div style={{ gridColumn: "1 / -1", padding: 14, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
              U varianty kremace bez obřadu + vystavení se řeší krematorium a zároveň údaje k vystavení zesnulého v síni.
            </div>
          ) : isKremaceBezObradu ? (
            <div style={{ gridColumn: "1 / -1", padding: 14, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
              U kremace bez obřadu se nevyplňují obřadní údaje. Důležité je hlavně krematorium a případná interní poznámka.
            </div>
          ) : null}
      </div>

      <div style={{ marginTop: 18 }}>
        <label style={labelStyle}>Poznámka</label>
        <textarea
          value={obrad.poznamka || ""}
          onChange={(e) => setField("poznamka", e.target.value)}
          style={textareaStyle}
        />
      </div>
    </div>
  );
}
